import { gql, useQuery } from '@apollo/client'

import { LegacySensorAlertsSetting } from './__generated__/types'
import { useSchemaFieldSupport } from './useSchemaFieldSupport'

export const GET_LEGACY_SENSOR_ALERTS_SETTING = gql`
  query LegacySensorAlertsSetting {
    me {
      id
      company {
        id
        companySettings {
          id
          legacySensorAlerts
        }
      }
    }
  }
`

/**
 * Per-company gate for the legacy fill-level sensor Alerts surfaces (PRO-560):
 * the top-level "Alerts" nav entry, the `/notifications/alerts` route, and the
 * Company settings → Alert rules section.
 *
 * DEPLOY-SAFE FAIL-OPEN — this inverts the usual fail-closed feature gate. The
 * backend field `CompanySettingsType.legacySensorAlerts` (beperf, built in
 * parallel) is not on the introspected schema yet, so it is read through a
 * STANDALONE query gated on a `useSchemaFieldSupport` probe: the query is
 * skipped — and therefore never sent to a lagging backend that would reject it
 * — until the live schema serves the field. Until then, and while the probe or
 * the query are in flight, the hook returns ENABLED, i.e. today's behaviour
 * (the surfaces stay visible, no nav flicker). Only an explicit `false` from a
 * backend that actually serves the field hides them. This fail-open contract
 * is exactly what makes the FE safe to merge before the BE.
 */
export const useLegacySensorAlertsEnabled = (): boolean => {
  const supported = useSchemaFieldSupport(
    'CompanySettingsType',
    'legacySensorAlerts'
  )
  const { data } = useQuery<LegacySensorAlertsSetting>(
    GET_LEGACY_SENSOR_ALERTS_SETTING,
    { skip: supported !== true, fetchPolicy: 'cache-first' }
  )

  // Field not on the live schema yet, or the probe is still in flight / failed
  // → fail open (this is the state on every backend until beperf's PR ships).
  if (supported !== true) return true
  const value = data?.me?.company?.companySettings?.legacySensorAlerts
  // Query still resolving, or the backend returned null → fail open.
  if (value === undefined || value === null) return true
  return value !== false
}
